import React, { useEffect, useRef, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { VizTheme, useVizTheme } from './theme';
import { PlotUtilities } from './renderers/types';

export enum AlertSeverity {
    INFO = 1,
    MINOR = 2,
    WARNING = 3,
    MAJOR = 4,
    CRITICAL = 5,
}

export interface AlertPeriod {
    id: string;
    startTime: number;
    endTime: number;
    severity?: AlertSeverity;
    color?: string;
}

export interface AlertBarConfig {
    onAlertHover?: (event: React.MouseEvent, data: { alertId: string }, isEntering: boolean) => void;
    onAlertClick?: (event: React.MouseEvent, data: { alertId: string }) => void;
}

export interface AlertBarProps {
    alertPeriods: AlertPeriod[];
    overallStartTime: number;
    overallEndTime: number;
    height?: number;
    width?: number;
    config?: AlertBarConfig;
}

const StyledAlertBarContainer = styled.div<{
    height: number;
    plotWidth?: number;
}>`
    position: absolute;
    bottom: -${props => props.height}px;
    left: 0;
    width: ${props => (props.plotWidth ? `${props.plotWidth}px` : '100%')};
    height: ${props => props.height}px;
    display: flex;
    align-items: stretch;
    background: ${props => (props.theme as VizTheme).alertBar.backgroundColor};
    overflow: hidden;
    z-index: 1;
`;

const AlertSegment = styled.div<{
    severity?: AlertSeverity;
    color?: string;
    width: number;
    left: number;
    isFirst?: boolean;
    isLast?: boolean;
}>`
    position: absolute;
    height: 100%;
    width: ${props => props.width}%;
    left: ${props => props.left}%;
    border-radius: 0;
    cursor: pointer;
    pointer-events: auto;
    box-sizing: border-box;
    ${props =>
        !props.isFirst ? `border-left: 3px solid ${(props.theme as VizTheme).alertBar.spacingColor};` : ''}
    ${props =>
        !props.isLast ? `border-right: 3px solid ${(props.theme as VizTheme).alertBar.spacingColor};` : ''}
    z-index: ${props => {
        // Higher severity = higher z-index (appears on top)
        switch (props.severity) {
            case AlertSeverity.CRITICAL:
                return 5;
            case AlertSeverity.MAJOR:
                return 4;
            case AlertSeverity.WARNING:
                return 3;
            case AlertSeverity.MINOR:
                return 2;
            case AlertSeverity.INFO:
                return 1;
            default:
                return 0;
        }
    }};
    background-color: ${props => {
        const theme = props.theme as VizTheme;
        return getAlertBarSegmentColor(theme, props.severity, props.color);
    }};

    &:hover {
        opacity: 0.8;
    }

    transition: opacity 0.2s ease;
`;

const SEVERITY_PRIORITIES = {
    [AlertSeverity.CRITICAL]: 0,
    [AlertSeverity.MAJOR]: 1,
    [AlertSeverity.WARNING]: 2,
    [AlertSeverity.MINOR]: 3,
    [AlertSeverity.INFO]: 4,
    default: 5,
} as const;

const DEFAULT_ALERT_BAR_HEIGHT = 12;
const DEFAULT_ALERT_BAR_WIDTH = 800;
const MIN_SEGMENT_WIDTH_PX = 10;

const getSeverityPriority = (severity?: AlertSeverity): number => {
    return SEVERITY_PRIORITIES[severity || 'default'] ?? SEVERITY_PRIORITIES.default;
};

const getAlertBarSegmentColor = (theme: VizTheme, severity?: AlertSeverity, color?: string) => {
    if (color) return color;
    switch (severity) {
        case AlertSeverity.CRITICAL:
            return theme.alertBar.severityColors.critical;
        case AlertSeverity.MAJOR:
            return theme.alertBar.severityColors.major;
        case AlertSeverity.WARNING:
            return theme.alertBar.severityColors.warning;
        case AlertSeverity.MINOR:
            return theme.alertBar.severityColors.minor;
        case AlertSeverity.INFO:
            return theme.alertBar.severityColors.info;
        default:
            return theme.alertBar.backgroundColor;
    }
};

// Helper function to calculate segments from periods array
const calculateSegments = (
    periods: AlertPeriod[],
    startTime: number,
    endTime: number,
    containerWidth: number = DEFAULT_ALERT_BAR_WIDTH
) => {
    const segments: Array<{
        period: AlertPeriod;
        width: number;
        left: number;
    }> = [];

    const totalDuration = endTime - startTime;

    // Sort periods by severity priority (critical first, then major, minor, etc.)
    const sortedPeriods = [...periods].sort((a, b) => {
        const priorityA = getSeverityPriority(a.severity);
        const priorityB = getSeverityPriority(b.severity);
        return priorityA - priorityB;
    });

    sortedPeriods.forEach(period => {
        const segmentStart = Math.max(period.startTime, startTime);
        const segmentEnd = Math.min(period.endTime, endTime);

        if (segmentStart < segmentEnd) {
            const left = ((segmentStart - startTime) / totalDuration) * 100;
            const width = ((segmentEnd - segmentStart) / totalDuration) * 100;

            // minimum width for accessibility
            const minWidthPercent = (MIN_SEGMENT_WIDTH_PX / containerWidth) * 100;
            const adjustedWidth = Math.max(width, minWidthPercent);

            segments.push({
                period,
                width: adjustedWidth,
                left,
            });
        }
    });

    return segments;
};

export const AlertBar = ({
    alertPeriods,
    overallStartTime,
    overallEndTime,
    height = DEFAULT_ALERT_BAR_HEIGHT,
    width = DEFAULT_ALERT_BAR_WIDTH,
    config,
}: AlertBarProps) => {
    const { onAlertHover, onAlertClick } = config || {};

    const handleMouseEnter = (event: React.MouseEvent, alertId: string) => {
        onAlertHover?.(event, { alertId }, true);
    };

    const handleMouseLeave = (event: React.MouseEvent, alertId: string) => {
        onAlertHover?.(event, { alertId }, false);
    };

    const handleClick = (event: React.MouseEvent, alertId: string) => {
        onAlertClick?.(event, { alertId });
    };

    if (alertPeriods?.length === 0) {
        return (
            <StyledAlertBarContainer height={height} plotWidth={width} data-test="alert-bar-container-empty">
                {/* Empty bar with gray background */}
            </StyledAlertBarContainer>
        );
    }

    const segments = calculateSegments(alertPeriods, overallStartTime, overallEndTime, width);

    return (
        <StyledAlertBarContainer height={height} plotWidth={width} data-test="alert-bar-container">
            {segments.map(segment => {
                const isAtAbsoluteLeftEdge = Math.abs(segment.left) < 0.01;
                const isAtAbsoluteRightEdge = Math.abs(segment.left + segment.width - 100) < 0.01;

                return (
                    <AlertSegment
                        key={segment.period.id}
                        severity={segment.period.severity}
                        color={segment.period.color}
                        width={segment.width}
                        left={segment.left}
                        isFirst={isAtAbsoluteLeftEdge}
                        isLast={isAtAbsoluteRightEdge}
                        data-test={`alert-segment-${segment.period.id}`}
                        onMouseEnter={event => handleMouseEnter(event, segment.period.id)}
                        onMouseLeave={event => handleMouseLeave(event, segment.period.id)}
                        onClick={event => handleClick(event, segment.period.id)}
                    />
                );
            })}
        </StyledAlertBarContainer>
    );
};

// Container component for integration with TimeSeriesChart
export interface AlertBarContainerProps {
    periods: AlertPeriod[];
    startTime: number;
    endTime: number;
    height?: number;
    width?: number;
    config?: AlertBarConfig;
    plotUtilities?: PlotUtilities;
}

export const AlertBarContainer = ({
    periods,
    startTime,
    endTime,
    height = DEFAULT_ALERT_BAR_HEIGHT,
    width,
    config,
    plotUtilities,
}: AlertBarContainerProps) => {
    // Reactively track the uPlot over-element's width via ResizeObserver so the
    // AlertBar always matches the plot area — even on initial render before the
    // legend has been measured and uPlot has resized.
    const [overWidth, setOverWidth] = useState<number | undefined>(undefined);
    const observerRef = useRef<ResizeObserver | null>(null);

    useEffect(() => {
        observerRef.current?.disconnect();
        observerRef.current = null;

        // plotUtilities.dimensions.over is a DOMRect snapshot — we need the live element
        // to observe size changes. The over element is inside the uPlot root.
        const el = plotUtilities?.el?.querySelector<HTMLElement>('.u-over');
        if (!el) {
            // Fall back to the snapshot width from plotUtilities if the live element
            // isn't available (e.g. in tests or before uPlot fully initialises).
            setOverWidth(plotUtilities?.dimensions?.over?.width);
            return undefined;
        }

        // Measure immediately
        setOverWidth(el.getBoundingClientRect().width);

        const observer = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect.width;
            if (w !== undefined) setOverWidth(w);
        });
        observer.observe(el);
        observerRef.current = observer;

        return () => observer.disconnect();
    }, [plotUtilities?.el, plotUtilities?.dimensions?.over?.width]);

    const alertBarWidth = overWidth || width || DEFAULT_ALERT_BAR_WIDTH;
    const theme = useVizTheme();

    return (
        <ThemeProvider theme={theme}>
            <AlertBar
                alertPeriods={periods}
                overallStartTime={startTime}
                overallEndTime={endTime}
                height={height}
                width={alertBarWidth}
                config={config}
            />
        </ThemeProvider>
    );
};
